import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { ShoppingCart } from 'lucide-react';
import { getCosClassName, getCpaClassName } from '../utils/metricsUtils';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
/**
 * Composant modulaire pour afficher les campagnes Performance Max qui ont diffusé des produits
 */
const PerformanceMaxProductsTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  token,
  setError,
  apiEndpoint = '/api/dashboard/components/performance-max-products',
  selectedCampaignFilter,
  pmaxLabelsFilter,
  campaignLabelsMap,
  performanceMaxChannelsData,
  compareEnabled = false,
  comparisonType = 'previous_period',
  isPmaxMetricVisible,
  moduleId = 'pmax_products'
}) => {

  // États locaux pour les données
  const [campaignsData, setCampaignsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const itemsPerPage = 15;

  // Calculer les IDs des campagnes filtrées
  const getFilteredCampaignIds = () => {
    if (!selectedCampaignFilter && (!pmaxLabelsFilter || pmaxLabelsFilter.length === 0)) {
      return null; // Pas de filtre actif
    }

    if (!performanceMaxChannelsData || performanceMaxChannelsData.length === 0) {
      return null;
    }

    const filteredIds = performanceMaxChannelsData
      .filter(campaign => {
        const campaignId = (campaign.id || campaign.campaignId || '').toString();
        const campaignName = campaign.name || campaign.campaignName || '';

        // Filtre par campagne spécifique
        if (selectedCampaignFilter) {
          const filterId = (selectedCampaignFilter.id || selectedCampaignFilter.campaignId || '').toString();
          const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
          if (campaignId !== filterId && campaignName !== filterName) {
            return false;
          }
        }

        // Filtre par labels
        if (pmaxLabelsFilter && pmaxLabelsFilter.length > 0) {
          const labels = (campaignLabelsMap && campaignLabelsMap[campaignId]) || [];
          if (!pmaxLabelsFilter.some(label => labels.includes(label))) {
            return false;
          }
        }

        return true;
      })
      .map(c => c.id || c.campaignId)
      .filter(Boolean);

    return filteredIds.length > 0 ? filteredIds : null;
  };

  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) return;

    const loadPerformanceMaxCampaigns = async () => {
      try {
        setLoading(true);
        const filteredCampaignIds = getFilteredCampaignIds();
        console.log('🚀 [PERFORMANCE-MAX-PRODUCTS-MODULAR] Chargement des campagnes Performance Max avec produits...',
          filteredCampaignIds ? `(${filteredCampaignIds.length} campagnes filtrées)` : '');

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          compareEnabled,
          comparisonType
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        // Ajouter les campaignIds filtrés si un filtre est actif
        if (filteredCampaignIds) {
          requestBody.campaignIds = filteredCampaignIds;
        }

        console.log('🚀 [PERFORMANCE-MAX-PRODUCTS-MODULAR] Requête vers:', `${config.API_URL}${apiEndpoint}`);
        console.log('🚀 [PERFORMANCE-MAX-PRODUCTS-MODULAR] Payload:', requestBody);

        const response = await axios.post(
          `${config.API_URL}${apiEndpoint}`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('🚀 [PERFORMANCE-MAX-PRODUCTS-MODULAR] Réponse reçue:', response.data);

        if (response.data.success && response.data.data) {
          setCampaignsData(response.data.data);
          setIsDataLoaded(true);
          console.log('✅ [PERFORMANCE-MAX-PRODUCTS-MODULAR] Données chargées:', {
            totalCampaigns: response.data.data.totalCampaigns,
            period: selectedPeriod
          });
        } else {
          console.error('❌ [PERFORMANCE-MAX-PRODUCTS-MODULAR] API retourne success=false:', response.data);
          setError('Erreur lors du chargement des campagnes Performance Max avec produits');
        }
      } catch (error) {
        console.error('❌ [PERFORMANCE-MAX-PRODUCTS-MODULAR] Erreur chargement:', error);
        console.error('❌ [PERFORMANCE-MAX-PRODUCTS-MODULAR] Détails:', error.response?.data || error.message);
        setError('Erreur lors du chargement des campagnes Performance Max avec produits');
      } finally {
        setLoading(false);
      }
    };

    loadPerformanceMaxCampaigns();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token, setError, selectedCampaignFilter, pmaxLabelsFilter, performanceMaxChannelsData?.length, compareEnabled, comparisonType]);

  // Réinitialiser la page lors du changement de données
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate]);

  // Ne pas afficher si pas de données et pas en chargement
  if (!loading && !isDataLoaded) return null;
  
  // Ne pas afficher si aucune campagne avec produits
  if (!loading && (!campaignsData?.campaigns || campaignsData.campaigns.length === 0)) return null;

  // Fonction de tri
  const sortedCampaigns = campaignsData?.campaigns ? [...campaignsData.campaigns].sort((a, b) => {
    let aValue, bValue;
    
    // Calcul spécial pour les métriques calculées
    switch(sortConfig.key) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      default:
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    }
  }) : [];

  // Fonction de gestion du tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Réinitialiser à la première page
  };

  // Calculer la pagination
  const totalItems = sortedCampaigns.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedCampaigns = sortedCampaigns.slice(startIndex, endIndex);

  // Calculer les totaux (sur toutes les campagnes, pas seulement la page actuelle)
  const calculateTotals = () => {
    if (!campaignsData || !campaignsData.campaigns) return null;

    return campaignsData.campaigns.reduce((acc, campaign) => ({
      impressions: acc.impressions + campaign.impressions,
      clicks: acc.clicks + campaign.clicks,
      cost: acc.cost + campaign.cost,
      conversions: acc.conversions + campaign.conversions,
      conversionsValue: acc.conversionsValue + campaign.conversionsValue
    }), {
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0
    });
  };

  const totals = calculateTotals();

  // Calculer les totaux de la période précédente si compareEnabled
  const calculatePreviousTotals = () => {
    if (!compareEnabled || !campaignsData || !campaignsData.campaigns) return null;

    const campaignsWithPrevious = campaignsData.campaigns.filter(c => c.previousPeriod);
    if (campaignsWithPrevious.length === 0) return null;

    return campaignsWithPrevious.reduce((acc, campaign) => ({
      impressions: acc.impressions + (campaign.previousPeriod?.impressions || 0),
      clicks: acc.clicks + (campaign.previousPeriod?.clicks || 0),
      cost: acc.cost + (campaign.previousPeriod?.cost || 0),
      conversions: acc.conversions + (campaign.previousPeriod?.conversions || 0),
      conversionsValue: acc.conversionsValue + (campaign.previousPeriod?.conversionsValue || 0)
    }), {
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0
    });
  };

  const previousTotals = calculatePreviousTotals();


  // Composant pour afficher la variation
  const renderVariation = makeRenderVariation({ fontSize: '11px' });

  // Formatter les dates pour l'affichage
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <div>
          <h4 style={{ marginBottom: compareEnabled && campaignsData?.comparisonPeriod ? '4px' : '0' }}><ShoppingCart size={16} />&nbsp;Performance Max - Canal Shopping</h4>
          {compareEnabled && campaignsData?.comparisonPeriod && (
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              Comparaison : {formatDateForDisplay(campaignsData.currentPeriod?.startDate)} - {formatDateForDisplay(campaignsData.currentPeriod?.endDate)}
              {' vs '}
              {formatDateForDisplay(campaignsData.comparisonPeriod?.startDate)} - {formatDateForDisplay(campaignsData.comparisonPeriod?.endDate)}
            </div>
          )}
        </div>
        <button
          onClick={onToggleVisibility}
          className="section-toggle-btn"
          title={isVisible ? "Masquer la section" : "Afficher la section"}
        >
          {isVisible ? '−' : '+'}
        </button>
      </div>

      {isVisible && (
        <>
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
              Chargement des campagnes Performance Max avec produits...<br /><br />
            </div>
          ) : campaignsData && campaignsData.campaigns && campaignsData.campaigns.length > 0 ? (
            <>
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" style={{ tableLayout: "auto" }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} className="sortable">
                        Campagne {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th>Statut</th>
                      {isPmaxMetricVisible('impressions') && (
                        <th onClick={() => handleSort('impressions')} className="sortable numeric">
                          Impr. {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('ctr') && (
                        <th onClick={() => handleSort('ctr')} className="sortable numeric">
                          CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('clics') && (
                        <th onClick={() => handleSort('clicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('cpc') && (
                        <th onClick={() => handleSort('cpc')} className="sortable numeric">
                          CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('cout') && (
                        <th onClick={() => handleSort('cost')} className="sortable numeric">
                          Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('taux_conv') && (
                        <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                          Taux Conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('conv') && (
                        <th onClick={() => handleSort('conversions')} className="sortable numeric">
                          Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('cpa') && (
                        <th onClick={() => handleSort('cpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('valeur_conv') && (
                        <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                          Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isPmaxMetricVisible('cos') && (
                        <th onClick={() => handleSort('cos')} className="sortable numeric">
                          COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCampaigns.map((campaign, index) => (
                      <tr key={`${campaign.id}-${index}`}>
                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {campaign.name}
                        </td>
                        <td>
                          <span style={{ 
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: campaign.statusText === 'Actif' ? '#DEF7EC' : 
                                           campaign.statusText === 'En pause' ? '#FEF3C7' : '#FEE2E2',
                            color: campaign.statusText === 'Actif' ? '#065F46' : 
                                   campaign.statusText === 'En pause' ? '#92400E' : '#991B1B'
                          }}>
                            {campaign.statusText}
                          </span>
                        </td>
                        {isPmaxMetricVisible('impressions') && (
                          <td className="numeric">
                            {formatNumber(campaign.impressions)}
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.impressions)}
                          </td>
                        )}
                        {isPmaxMetricVisible('ctr') && (
                          <td className="numeric">
                            {formatNumber(campaign.ctr, 2)}%
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.ctr)}
                          </td>
                        )}
                        {isPmaxMetricVisible('clics') && (
                          <td className="numeric">
                            {formatNumber(campaign.clicks)}
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.clicks)}
                          </td>
                        )}
                        {isPmaxMetricVisible('cpc') && (
                          <td className="numeric">
                            {formatNumber(campaign.cpc, 2)}€
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.cpc, true)}
                          </td>
                        )}
                        {isPmaxMetricVisible('cout') && (
                          <td className="numeric">
                            {formatNumber(campaign.cost, 2)}€
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.cost)}
                          </td>
                        )}
                        {isPmaxMetricVisible('taux_conv') && (
                          <td className="numeric">
                            {formatNumber(campaign.conversionRate, 2)}%
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.conversionRate)}
                          </td>
                        )}
                        {isPmaxMetricVisible('conv') && (
                          <td className="numeric">
                            {formatNumber(campaign.conversions, 1)}
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.conversions)}
                          </td>
                        )}
                        {isPmaxMetricVisible('cpa') && (
                          <td className={`numeric ${getCpaClassName(campaign.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                            {campaign.conversions > 0 ? `${formatNumber(campaign.cpa, 2)}€` : '∞'}
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.cpa, true)}
                          </td>
                        )}
                        {isPmaxMetricVisible('valeur_conv') && (
                          <td className="numeric">
                            {formatNumber(campaign.conversionsValue, 2)}€
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.conversionsValue)}
                          </td>
                        )}
                        {isPmaxMetricVisible('cos') && (
                          <td className={`numeric ${getCosClassName(campaign.cos, selectedAccount?.id || 'global', moduleId)}`}>
                            {formatNumber(campaign.cos, 1)}%
                            {compareEnabled && campaign.variations && renderVariation(campaign.variations.cos, true)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  {totals && (
                    <tfoot>
                      <tr className="total-row">
                        <td><strong>Total ({campaignsData.campaigns.length} campagnes)</strong></td>
                        <td></td>
                        {isPmaxMetricVisible('impressions') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.impressions)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
                          </td>
                        )}
                        {isPmaxMetricVisible('ctr') && (
                          <td className="numeric">
                            <strong>{totals.impressions > 0 ? formatNumber((totals.clicks / totals.impressions * 100), 2) + '%' : formatNumber(0, 2) + '%'}</strong>
                            {compareEnabled && previousTotals && renderVariation(
                              calculateVariation(
                                totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0,
                                previousTotals.impressions > 0 ? (previousTotals.clicks / previousTotals.impressions * 100) : 0
                              )
                            )}
                          </td>
                        )}
                        {isPmaxMetricVisible('clics') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.clicks)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
                          </td>
                        )}
                        {isPmaxMetricVisible('cpc') && (
                          <td className="numeric">
                            <strong>{totals.clicks > 0 ? formatNumber((totals.cost / totals.clicks), 2) + '€' : formatNumber(0, 2) + '€'}</strong>
                            {compareEnabled && previousTotals && renderVariation(
                              calculateVariation(
                                totals.clicks > 0 ? (totals.cost / totals.clicks) : 0,
                                previousTotals.clicks > 0 ? (previousTotals.cost / previousTotals.clicks) : 0
                              ), true
                            )}
                          </td>
                        )}
                        {isPmaxMetricVisible('cout') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.cost, 2)}€</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost))}
                          </td>
                        )}
                        {isPmaxMetricVisible('taux_conv') && (
                          <td className="numeric">
                            <strong>{totals.clicks > 0 ? formatNumber((totals.conversions / totals.clicks * 100), 2) + '%' : formatNumber(0, 2) + '%'}</strong>
                            {compareEnabled && previousTotals && renderVariation(
                              calculateVariation(
                                totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0,
                                previousTotals.clicks > 0 ? (previousTotals.conversions / previousTotals.clicks * 100) : 0
                              )
                            )}
                          </td>
                        )}
                        {isPmaxMetricVisible('conv') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.conversions, 1)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
                          </td>
                        )}
                        {isPmaxMetricVisible('cpa') && (
                          <td className={`numeric ${getCpaClassName(totals.conversions > 0 ? (totals.cost / totals.conversions) : 0, selectedAccount?.id || 'global', moduleId)}`}>
                            <strong>{totals.conversions > 0 ? formatNumber((totals.cost / totals.conversions), 2) + '€' : '∞'}</strong>
                            {compareEnabled && previousTotals && renderVariation(
                              calculateVariation(
                                totals.conversions > 0 ? (totals.cost / totals.conversions) : 0,
                                previousTotals.conversions > 0 ? (previousTotals.cost / previousTotals.conversions) : 0
                              ), true
                            )}
                          </td>
                        )}
                        {isPmaxMetricVisible('valeur_conv') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.conversionsValue, 2)}€</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
                          </td>
                        )}
                        {isPmaxMetricVisible('cos') && (
                          <td className={`numeric ${getCosClassName(totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0, selectedAccount?.id || 'global', moduleId)}`}>
                            <strong>{totals.conversionsValue > 0 ? formatNumber((totals.cost / totals.conversionsValue * 100), 1) + '%' : '∞'}</strong>
                            {compareEnabled && previousTotals && renderVariation(
                              calculateVariation(
                                totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0,
                                previousTotals.conversionsValue > 0 ? (previousTotals.cost / previousTotals.conversionsValue * 100) : 0
                              ), true
                            )}
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              
              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {startIndex + 1}-{endIndex} sur {totalItems} campagnes
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              Aucune campagne Performance Max avec produits trouvée pour la période sélectionnée
            </div>
          )}
        </>
      )}
    </div>
  );
});

PerformanceMaxProductsTableModular.displayName = 'PerformanceMaxProductsTableModular';

export default PerformanceMaxProductsTableModular;